import { useEffect, useState } from 'react'
import { adminApi } from '../../services'
import { listQcDashboardAlerts } from '../../services/qc'
import type { User } from '../../types'
import type { DashboardAlert, OrgCasesSummary, OrgStorageSummary } from '../../types/dashboard'
import type { QcDashboardEntry } from '../../types/qc'
import { logger } from '../../utils/logger'

const RECENT_USERS_LIMIT = 5
const FETCH_PAGE_LIMIT = 100
const MS_PER_SECOND = 1000
const SECONDS_PER_MINUTE = 60
const MINUTES_PER_HOUR = 60
const HOURS_PER_DAY = 24
const MS_PER_DAY = MS_PER_SECOND * SECONDS_PER_MINUTE * MINUTES_PER_HOUR * HOURS_PER_DAY
const EXPIRY_DAYS = 30
const CRITICAL_EXPIRY_DAYS = 7
const STORAGE_WARNING_PERCENT = 80
const STORAGE_CRITICAL_PERCENT = 95

export interface DashboardStats {
  users: number
  organisations: number
  licenses: number
  activeLicenses: number
}

export interface DashboardData {
  stats: DashboardStats
  recentUsers: User[]
  loading: boolean
  alertsLoading: boolean
  alerts: DashboardAlert[]
  usersByStatus: Record<string, number>
  usersByRole: Record<string, number>
  orgName: string | null
  casesSummary: OrgCasesSummary | null
  storageSummary: OrgStorageSummary | null
}

const INITIAL_STATS: DashboardStats = { users: 0, organisations: 0, licenses: 0, activeLicenses: 0 }

interface AdminListResponse<T> {
  data: T[]
  total: number
}

interface AdminListsResult {
  usersRes: AdminListResponse<User>
  orgsRes: AdminListResponse<{ id: string }> | null
  licensesRes: AdminListResponse<{ status: string; validUntil?: string | null }> | null
}

async function fetchAdminLists(isPlatformAdmin: boolean): Promise<AdminListsResult> {
  const [usersRes, orgsRes, licensesRes] = await Promise.all([
    adminApi.listUsers({ limit: FETCH_PAGE_LIMIT }),
    isPlatformAdmin
      ? adminApi.listOrganisations({ limit: FETCH_PAGE_LIMIT })
      : Promise.resolve(null),
    isPlatformAdmin ? adminApi.listLicenses({ limit: FETCH_PAGE_LIMIT }) : Promise.resolve(null),
  ])
  return { usersRes, orgsRes, licensesRes }
}

interface UserBreakdown {
  byStatus: Record<string, number>
  byRole: Record<string, number>
  pendingCount: number
}

function computeUserBreakdown(users: User[]): UserBreakdown {
  const byStatus = {
    active: users.filter((user) => user.status === 'active').length,
    pending: users.filter((user) => user.status === 'pending').length,
    suspended: users.filter((user) => user.status === 'suspended').length,
  }
  const byRole: Record<string, number> = {}
  for (const user of users) {
    byRole[user.role] = (byRole[user.role] ?? 0) + 1
  }
  return { byStatus, byRole, pendingCount: byStatus.pending }
}

interface LicenseEntry {
  status: string
  validUntil?: string | null
}

function findExpiringLicenses(licenses: LicenseEntry[], now: Date): LicenseEntry[] {
  const cutoff = new Date(now.getTime() + EXPIRY_DAYS * MS_PER_DAY)
  return licenses.filter((license) => {
    if (license.status !== 'active' || !license.validUntil) return false
    return new Date(license.validUntil) <= cutoff
  })
}

function hasCriticalExpiry(licenses: LicenseEntry[], now: Date): boolean {
  return licenses.some((license) => {
    if (!license.validUntil) return false
    const daysUntil = Math.ceil(
      (new Date(license.validUntil).getTime() - now.getTime()) / MS_PER_DAY
    )
    return daysUntil <= CRITICAL_EXPIRY_DAYS
  })
}

function buildPendingUsersAlert(pendingCount: number): DashboardAlert | null {
  if (pendingCount <= 0) return null
  return {
    id: 'pending-users',
    type: 'users_pending_activation',
    severity: 'warning',
    title: 'users pending activation',
    count: pendingCount,
    actionLabel: 'Review and activate users',
    actionPath: '/admin/users?status=pending',
  }
}

function buildExpiringLicenseAlert(licenses: LicenseEntry[]): DashboardAlert | null {
  const now = new Date()
  const expiring = findExpiringLicenses(licenses, now)
  if (expiring.length === 0) return null
  return {
    id: 'expiring-licenses',
    type: 'license_expiring',
    severity: hasCriticalExpiry(expiring, now) ? 'critical' : 'warning',
    title: 'licenses expiring within 30 days',
    count: expiring.length,
    actionLabel: 'Review and renew licenses',
    actionPath: '/admin/licenses',
  }
}

function buildCriticalCasesAlert(cases: OrgCasesSummary): DashboardAlert | null {
  const critical = cases.byPriority.critical
  if (!critical || critical <= 0) return null
  return {
    id: 'critical-cases',
    type: 'cases_critical',
    severity: 'critical',
    title: 'critical priority cases',
    count: critical,
    actionLabel: 'Review critical cases',
    actionPath: '/cases?priority=critical',
  }
}

function buildStorageAlert(storage: OrgStorageSummary): DashboardAlert | null {
  if (!storage.quotaBytes || storage.percentUsed < STORAGE_WARNING_PERCENT) return null
  return {
    id: 'storage-warning',
    type: 'storage_quota_warning',
    severity: storage.percentUsed >= STORAGE_CRITICAL_PERCENT ? 'critical' : 'warning',
    title: `storage quota at ${String(Math.round(storage.percentUsed))}%`,
    count: 1,
    actionLabel: 'Review storage usage',
    actionPath: '/admin/storage',
  }
}

/**
 * Consolidate one or more `QcDashboardEntry` rows into a single dashboard
 * alert. Per PRP Component 8 (DEFINITIVE): one consolidated alert per user,
 * not one per failing run — avoids dashboard overflow when several
 * collections fail concurrently. `actionPath` points at the most recent
 * failing run for one-click drill-down.
 */
function buildQcFailedAlert(entries: QcDashboardEntry[]): DashboardAlert | null {
  if (entries.length === 0) return null
  const totalUnacked = entries.reduce((sum, e) => sum + e.unackedCount, 0)
  return {
    id: 'qc-failed',
    type: 'qc_failed',
    severity: 'critical',
    title: 'QC check failures',
    count: totalUnacked,
    actionLabel: 'Review QC failures',
    actionPath: entries[0]?.runDetailPath ?? '/cases',
  }
}

/**
 * Best-effort fetch of the QC dashboard alert source. Failures are logged
 * and result in an empty array — they must never break the dashboard load.
 */
async function fetchQcAlerts(): Promise<DashboardAlert[]> {
  try {
    const response = await listQcDashboardAlerts()
    const alert = buildQcFailedAlert(response.entries)
    return alert ? [alert] : []
  } catch (error) {
    logger.error('useDashboardData', 'Failed to fetch QC dashboard alerts', error)
    return []
  }
}

interface OrgFetchResult {
  orgName: string | null
  casesSummary: OrgCasesSummary | null
  storageSummary: OrgStorageSummary | null
  alerts: DashboardAlert[]
}

async function fetchOrgData(organisationId: string): Promise<OrgFetchResult> {
  let orgName: string | null = null
  try {
    const org = await adminApi.getOrganisation(organisationId)
    orgName = org.name
  } catch {
    // ignore
  }
  try {
    const [casesData, storageData] = await Promise.all([
      adminApi.getOrgCasesSummary(),
      adminApi.getOrgStorageSummary(),
    ])
    const alerts: DashboardAlert[] = []
    const criticalAlert = buildCriticalCasesAlert(casesData)
    if (criticalAlert) alerts.push(criticalAlert)
    const storageAlert = buildStorageAlert(storageData)
    if (storageAlert) alerts.push(storageAlert)
    return { orgName, casesSummary: casesData, storageSummary: storageData, alerts }
  } catch (error) {
    logger.error('useDashboardData', 'Failed to fetch org dashboard data', error)
    return { orgName, casesSummary: null, storageSummary: null, alerts: [] }
  }
}

interface BuildAdminAlertsArgs {
  pendingCount: number
  licenses: LicenseEntry[] | null
}

function buildAdminAlerts(args: BuildAdminAlertsArgs): DashboardAlert[] {
  const alerts: DashboardAlert[] = []
  const pending = buildPendingUsersAlert(args.pendingCount)
  if (pending) alerts.push(pending)
  if (args.licenses) {
    const expiring = buildExpiringLicenseAlert(args.licenses)
    if (expiring) alerts.push(expiring)
  }
  return alerts
}

interface AdminFetchSetters {
  setStats: (next: DashboardStats) => void
  setRecentUsers: (next: User[]) => void
  setAlerts: (next: DashboardAlert[]) => void
  setUsersByStatus: (next: Record<string, number>) => void
  setUsersByRole: (next: Record<string, number>) => void
  setOrgName: (next: string | null) => void
  setCasesSummary: (next: OrgCasesSummary | null) => void
  setStorageSummary: (next: OrgStorageSummary | null) => void
}

interface AdminFetchFlags {
  isPlatformAdmin: boolean
  organisationId: string | null
}

function applyAdminLists(
  lists: AdminListsResult,
  setters: AdminFetchSetters,
  qcAlerts: DashboardAlert[]
): { adminAlerts: DashboardAlert[] } {
  const breakdown = computeUserBreakdown(lists.usersRes.data)
  setters.setUsersByStatus(breakdown.byStatus)
  setters.setUsersByRole(breakdown.byRole)
  setters.setStats({
    users: lists.usersRes.total,
    organisations: lists.orgsRes?.total ?? 0,
    licenses: lists.licensesRes?.total ?? 0,
    activeLicenses:
      lists.licensesRes?.data.filter((license) => license.status === 'active').length ?? 0,
  })
  setters.setRecentUsers(lists.usersRes.data.slice(0, RECENT_USERS_LIMIT))
  const adminAlerts = buildAdminAlerts({
    pendingCount: breakdown.pendingCount,
    licenses: lists.licensesRes?.data ?? null,
  })
  setters.setAlerts([...adminAlerts, ...qcAlerts])
  return { adminAlerts }
}

interface ApplyOrgDataArgs {
  organisationId: string
  setters: AdminFetchSetters
  adminAlerts: DashboardAlert[]
  qcAlerts: DashboardAlert[]
}

async function applyOrgData(args: ApplyOrgDataArgs): Promise<void> {
  const { organisationId, setters, adminAlerts, qcAlerts } = args
  const orgData = await fetchOrgData(organisationId)
  setters.setOrgName(orgData.orgName)
  setters.setCasesSummary(orgData.casesSummary)
  setters.setStorageSummary(orgData.storageSummary)
  setters.setAlerts([...adminAlerts, ...orgData.alerts, ...qcAlerts])
}

async function runDashboardFetch(
  setters: AdminFetchSetters,
  flags: AdminFetchFlags
): Promise<void> {
  try {
    // QC alerts are fetched in parallel with admin lists — both are bounded
    // single round-trips and the QC fetch failure is non-fatal.
    const [lists, qcAlerts] = await Promise.all([
      fetchAdminLists(flags.isPlatformAdmin),
      fetchQcAlerts(),
    ])
    const { adminAlerts } = applyAdminLists(lists, setters, qcAlerts)
    if (!flags.isPlatformAdmin && flags.organisationId) {
      await applyOrgData({
        organisationId: flags.organisationId,
        setters,
        adminAlerts,
        qcAlerts,
      })
    }
  } catch (error) {
    logger.error('useDashboardData', 'Failed to fetch dashboard data', error)
  }
}

interface UseDashboardDataInput {
  user: User | null | undefined
  isPlatformAdmin: boolean
  isAdmin: boolean
}

export function useDashboardData(input: UseDashboardDataInput): DashboardData {
  const { user, isPlatformAdmin, isAdmin } = input
  const [stats, setStats] = useState<DashboardStats>(INITIAL_STATS)
  const [recentUsers, setRecentUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [alertsLoading, setAlertsLoading] = useState(true)
  const [alerts, setAlerts] = useState<DashboardAlert[]>([])
  const [usersByStatus, setUsersByStatus] = useState<Record<string, number>>({})
  const [usersByRole, setUsersByRole] = useState<Record<string, number>>({})
  const [orgName, setOrgName] = useState<string | null>(null)
  const [casesSummary, setCasesSummary] = useState<OrgCasesSummary | null>(null)
  const [storageSummary, setStorageSummary] = useState<OrgStorageSummary | null>(null)

  useEffect(() => {
    if (!isAdmin) {
      setLoading(false)
      setAlertsLoading(false)
      return
    }
    const setters: AdminFetchSetters = {
      setStats,
      setRecentUsers,
      setAlerts,
      setUsersByStatus,
      setUsersByRole,
      setOrgName,
      setCasesSummary,
      setStorageSummary,
    }
    const flags: AdminFetchFlags = {
      isPlatformAdmin,
      organisationId: user?.organisationId ?? null,
    }
    void runDashboardFetch(setters, flags).finally(() => {
      setLoading(false)
      setAlertsLoading(false)
    })
  }, [isPlatformAdmin, isAdmin, user?.organisationId])

  return {
    stats,
    recentUsers,
    loading,
    alertsLoading,
    alerts,
    usersByStatus,
    usersByRole,
    orgName,
    casesSummary,
    storageSummary,
  }
}
